import type { PageSection } from "@shared/entities";
import { PAGE_SECTIONS } from "@shared/entities";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import toast from "react-hot-toast";
import { PageHeader } from "../components/page-state";
import { ApiError, errorMessage } from "../lib/api";
import { createPage, invalidatePages } from "../lib/queries";

export const Route = createFileRoute("/pages/new")({ component: NewPage });

const SECTION_LABEL: Record<PageSection, string> = { design: "Design", systems: "Systems", qa: "QA", chapters: "Chapters", other: "Other" };

const slugify = (s: string) =>
	s
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, 120);

function NewPage() {
	const navigate = useNavigate();
	const [title, setTitle] = useState("");
	const [slug, setSlug] = useState("");
	const [slugTouched, setSlugTouched] = useState(false);
	const [section, setSection] = useState<PageSection>("design");
	const [errors, setErrors] = useState<Record<string, string[]>>({});
	const [saving, setSaving] = useState(false);

	async function submit() {
		setSaving(true);
		setErrors({});
		try {
			const page = await createPage({ title: title.trim(), slug, section, body: `# ${title.trim()}\n\n` });
			await invalidatePages();
			toast.success("Page created");
			void navigate({ to: "/pages/$slug", params: { slug: page.slug }, search: { edit: true } });
		} catch (err) {
			if (err instanceof ApiError && err.code === "VALIDATION_FAILED") setErrors(err.fieldErrors);
			else if (err instanceof ApiError && err.code === "CONFLICT") setErrors({ slug: [err.message] });
			else toast.error(errorMessage(err));
		} finally {
			setSaving(false);
		}
	}

	return (
		<div className="max-w-xl">
			<PageHeader title="New page" eyebrow="A design page, in markdown" />
			<form
				className="panel space-y-4 p-5"
				onSubmit={(e) => {
					e.preventDefault();
					void submit();
				}}
			>
				<div>
					<label className="label" htmlFor="title">
						Title
					</label>
					<input
						id="title"
						className="field"
						value={title}
						autoFocus
						onChange={(e) => {
							setTitle(e.target.value);
							if (!slugTouched) setSlug(slugify(e.target.value));
						}}
						placeholder="Chapter 3 boss fight"
					/>
					{errors["title"] && <p className="mt-1 text-xs" style={{ color: "var(--color-bad)" }}>{errors["title"].join(" ")}</p>}
				</div>
				<div>
					<label className="label" htmlFor="slug">
						Address <span style={{ color: "var(--color-ink-400)" }}>/pages/…</span>
					</label>
					<input
						id="slug"
						className="field font-mono"
						value={slug}
						onChange={(e) => {
							setSlugTouched(true);
							setSlug(slugify(e.target.value));
						}}
					/>
					{errors["slug"] && <p className="mt-1 text-xs" style={{ color: "var(--color-bad)" }}>{errors["slug"].join(" ")}</p>}
				</div>
				<div>
					<label className="label" htmlFor="section">
						Section
					</label>
					<select id="section" className="field" value={section} onChange={(e) => setSection(e.target.value as PageSection)}>
						{PAGE_SECTIONS.map((s) => (
							<option key={s} value={s}>
								{SECTION_LABEL[s]}
							</option>
						))}
					</select>
				</div>
				<div className="flex justify-end gap-2">
					<button type="button" className="btn" onClick={() => history.back()}>
						Cancel
					</button>
					<button type="submit" className="btn btn-primary" disabled={saving || !title.trim() || !slug}>
						{saving ? "Creating…" : "Create page"}
					</button>
				</div>
			</form>
		</div>
	);
}
